import {
    Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
    TextField, Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useEffect, useMemo, useRef, useState } from 'react';
import MDButton from '@components/MDButton';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { LabelledSelect } from '@components/StandaloneAutoCompleteSelect/labelled-select';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { WhatsappTemplateService } from '../services/whatsapp-template.service';
import {
    LeadVariableCategory,
    WhatsappTemplateResponse,
} from '../services/responses/whatsapp-template.response';
import { WhatsappTemplateHelp } from './whatsapp-template.help';
import { VariableChips } from '@components/MessageField/variable-chips';

interface Props {
    open: boolean;
    edit: WhatsappTemplateResponse | null;
    onClose: () => void;
    onSaved: () => void;
}

const LANGUAGES = ['pt_BR', 'en_US', 'es_ES'];
const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];

// Mesma leitura do backend, para cobrar as mesmas variáveis que a Meta.
const readVariables = (text: string): string[] => {
    const found: string[] = [];
    let position = 0;

    while (position < text.length) {
        const start = text.indexOf('{{', position);
        if (start < 0) break;

        const end = text.indexOf('}}', start + 2);
        if (end < 0) break;

        const name = text.slice(start + 2, end).trim();
        if (name && !found.includes(name)) found.push(name);

        position = end + 2;
    }

    return found;
};

// A Meta só aceita minúscula, dígito e sublinhado, começando por letra.
const validName = (name: string): boolean => {
    if (!name) return false;
    if (name[0] < 'a' || name[0] > 'z') return false;

    for (const c of name) {
        const ok = (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === '_';
        if (!ok) return false;
    }

    return true;
};

export const WhatsappTemplateModal = ({ open, edit, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [controller, dispatch] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const service = new WhatsappTemplateService();

    const bodyRef = useRef<HTMLTextAreaElement>(null);

    const [name, setName] = useState('');
    const [templateLanguage, setLanguage] = useState('pt_BR');
    const [templateCategory, setCategory] = useState('MARKETING');
    const [body, setBody] = useState('');
    const [sampleValues, setExamples] = useState<Record<string, string>>({});
    const [categories, setCategories] = useState<LeadVariableCategory[]>([]);
    const [helpOpen, setHelpOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // Chave para o nome que a pessoa reconhece: o aviso lista "Nome do contato", não "lead_name".
    const doLead = useMemo(
        () => new Map(categories.flatMap((c) => c.variables.map((v) => [v.key, v.label] as const))),
        [categories]
    );

    const variables = useMemo(() => readVariables(body), [body]);
    const invalid = useMemo(() => variables.filter((v) => !validName(v)), [variables]);

    // A Meta recusa variável abrindo ou fechando o texto; avisar ao digitar poupa a ida e volta da análise.
    const variableAtEdge = useMemo(() => {
        const text = body.trim();
        return text.startsWith('{{') || text.endsWith('}}');
    }, [body]);

    // Variável que o CRM conhece não pede exemplo: o dado do lead entra no envio.
    const unfilled = useMemo(
        () => variables.filter((v) => validName(v) && !doLead.has(v)),
        [variables, doLead]
    );

    useEffect(() => {
        if (!open || categories.length > 0) return;

        service
            .getVariables()
            .then((res) => setCategories(res.data.data))
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000));
    }, [open]);

    useEffect(() => {
        if (!open) return;

        setName(edit?.name ?? '');
        setLanguage(edit?.language ?? 'pt_BR');
        setCategory(edit?.category ?? 'MARKETING');
        setBody(edit?.bodyText ?? '');
        setExamples({});
    }, [open]);

    // Insere no cursor, não no fim: quem escreve no meio da frase perderia o lugar.
    const insertVariable = (key: string) => {
        const field = bodyRef.current;
        const marker = `{{${key}}}`;

        if (!field) {
            setBody((current) => current + marker);
            return;
        }

        const start = field.selectionStart ?? body.length;
        const end = field.selectionEnd ?? body.length;
        const before = body.slice(0, start);
        const after = body.slice(end);

        // Variáveis coladas ("{{a}}{{b}}") viram uma palavra só: o espaço entra só onde falta.
        const text = (before && !/\s$/.test(before) ? ' ' : '')
            + marker
            + (after && !/^\s/.test(after) ? ' ' : '');

        setBody(before + text + after);

        setTimeout(() => {
            field.focus();
            field.setSelectionRange(start + text.length, start + text.length);
        }, 0);
    };

    const save = async () => {
        if (invalid.length > 0) {
            showAlert(
                t('whatsappTemplates.nomeVariavelInvalido', { lista: invalid.join(', ') }),
                'error',
                6000
            );
            return;
        }

        const missing = unfilled.filter((v) => !sampleValues[v]?.trim());

        if (missing.length > 0) {
            showAlert(
                t('whatsappTemplates.faltaExemplo', { lista: missing.join(', ') }),
                'error',
                5000
            );
            return;
        }

        setSaving(true);
        setLoading(dispatch, true);

        const requestBody = {
            name,
            language: templateLanguage,
            category: templateCategory,
            bodyText: body,
            examples: sampleValues,
        };

        try {
            if (edit) {
                await service.update(edit.id, requestBody, edit.componentsJson);
            } else {
                await service.create(requestBody);
            }

            showAlert(t('whatsappTemplates.enviadoParaAprovacao'), 'success', 4000);
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 6000);
        } finally {
            setSaving(false);
            setLoading(dispatch, false);
        }
    };

    const dirty = name !== (edit?.name ?? '') || body !== (edit?.bodyText ?? '');

    // Esc e clique fora fechavam com o texto digitado; com algo escrito, só o Cancelar fecha.
    const closeUnlessDirty = (_: unknown, reason: 'escapeKeyDown' | 'backdropClick') => {
        if (dirty && (reason === 'escapeKeyDown' || reason === 'backdropClick')) return;

        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={closeUnlessDirty}
                fullWidth
                maxWidth="lg"
                scroll="paper"
                // Preso no topo: centralizado, cada aviso novo subia o modal e o clique seguinte errava.
                sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {edit ? t('whatsappTemplates.editarTitulo') : t('whatsappTemplates.novoTitulo')}
                    <MDButton
                        variant="text"
                        color="info"
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        onClick={() => setHelpOpen(true)}
                    >
                        {t('whatsappTemplates.comoFunciona')}
                    </MDButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {/* Rótulo em cima nos três: o react-select não tem label flutuante e misturar desalinha a linha. */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <LabelledSelect cc={cc} label={t('whatsappTemplates.nome')}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        disabled={!!edit}
                                        helperText={
                                            edit
                                                ? t('whatsappTemplates.nomeImutavel')
                                                : t('whatsappTemplates.nomeAjuda')
                                        }
                                        autoComplete="off"
                                        // Mesma altura e fonte dos selects de idioma e categoria (43px no react-select).
                                        sx={{
                                            '& .MuiOutlinedInput-root': { height: 43 },
                                            '& .MuiOutlinedInput-input': { fontSize: 14 },
                                        }}
                                    />
                                </LabelledSelect>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <LabelledSelect cc={cc} label={t('whatsappTemplates.idioma')}>
                                    <StandaloneAutoCompleteSelect
                                        options={LANGUAGES.map((item) => ({
                                            value: item,
                                            label: t(`whatsappTemplates.idiomas.${item}`),
                                        }))}
                                        value={templateLanguage}
                                        onChange={(value) => setLanguage(value as string)}
                                        disabled={!!edit}
                                    />
                                </LabelledSelect>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <LabelledSelect cc={cc} label={t('whatsappTemplates.categoria')}>
                                    <StandaloneAutoCompleteSelect
                                        options={CATEGORIES.map((item) => ({
                                            value: item,
                                            label: t(`whatsappTemplates.categorias.${item}`),
                                        }))}
                                        value={templateCategory}
                                        onChange={(value) => setCategory(value as string)}
                                    />
                                </LabelledSelect>
                            </FormControl>
                        </Grid>

                        <Grid size={12}>
                            <FormControl fullWidth>
                                <TextField
                                    multiline
                                    minRows={6}
                                    size="small"
                                    inputRef={bodyRef}
                                    label={t('whatsappTemplates.corpo')}
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    helperText={t('whatsappTemplates.corpoAjuda')}
                                    inputProps={{ maxLength: 1024 }}
                                />
                                <VariableChips onInsert={insertVariable} size="large" />
                            </FormControl>
                        </Grid>

                        {invalid.length > 0 && (
                            <Grid size={12}>
                                <Alert severity="error" icon={false}>
                                    <Typography variant="caption">
                                        {t('whatsappTemplates.nomeVariavelInvalido', {
                                            lista: invalid.join(', '),
                                        })}
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {variableAtEdge && (
                            <Grid size={12}>
                                <Alert severity="error" icon={false}>
                                    <Typography variant="caption">
                                        {t('whatsappTemplates.variavelNasPontas')}
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {variables.some((v) => doLead.has(v)) && (
                            <Grid size={12}>
                                <Alert severity="success" icon={false}>
                                    <Typography variant="caption">
                                        {t('whatsappTemplates.preenchidasSozinhas', {
                                            lista: variables.filter((v) => doLead.has(v)).map((v) => doLead.get(v)).join(', '),
                                        })}
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {unfilled.length > 0 && (
                            <Grid size={12}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {t('whatsappTemplates.exemplosAjuda')}
                                </Typography>
                                <Box sx={{ display: 'grid', gap: 1.5, mt: 1 }}>
                                    {unfilled.map((variable) => (
                                        <TextField
                                            key={variable}
                                            size="small"
                                            label={'{{' + variable + '}}'}
                                            value={sampleValues[variable] ?? ''}
                                            onChange={(e) =>
                                                setExamples((current) => ({
                                                    ...current,
                                                    [variable]: e.target.value,
                                                }))
                                            }
                                            autoComplete="off"
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}

                        <Grid size={12}>
                            <Alert severity="info" icon={false}>
                                <Typography variant="caption">
                                    {edit
                                        ? t('whatsappTemplates.avisoEdicao')
                                        : t('whatsappTemplates.avisoCriacao')}
                                </Typography>
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <MDButton
                        sx={{ marginRight: '5px' }}
                        variant="gradient"
                        color="dark"
                        onClick={onClose}
                        disabled={saving}
                    >
                        {t('general.cancelar')}
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="success"
                        onClick={save}
                        disabled={saving || !name.trim() || !body.trim() || invalid.length > 0 || variableAtEdge}
                    >
                        {t('whatsappTemplates.enviarParaMeta')}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <WhatsappTemplateHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
        </>
    );
};
